/**
 * Items list — V1.0.x catalogue surface (ADR-0013 redesign).
 *
 * Mirrors `prototype/catalogue/items.html` verbatim:
 *   • 5-KPI strip (active, avg margin, reorder watchlist, catalogue health, stale)
 *   • 6 tabs (all, goods, services, low_stock, untagged, archived)
 *   • 9-column table (item, category, hsn, stock, cost, rate, margin, vendor, updated)
 *   • Bulk-action bar revealed on row select
 *   • List ↔ grid view toggle
 *
 * Class names are kept verbatim from the prototype so the
 * pixel-fidelity discipline (playbook §6) holds. All styling is
 * scoped under the `.items-list-page` root in `items-list-page.css`.
 *
 * @see prototype/catalogue/items.html
 * @see docs/adr/0013-items-field-expansion.md
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import {
  ChevronRight,
  Download,
  Filter,
  Hammer,
  LayoutGrid,
  List as ListIcon,
  Package,
  Plus,
  Search,
  SlidersHorizontal,
  Upload,
} from "lucide-react";
import type { items as itemsContract } from "@flint/api-contract";
import type { z } from "zod";
import {
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRoot,
  DropdownMenuTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  TabsContent,
  TabsList,
  TabsRoot,
  TabsTrigger,
  toast,
} from "@flint/ui";
import { DataTableCard, ListPagination, avatarClassFor, initialsFor } from "../../master-list/index.ts";
import { trpc } from "../../../lib/trpc.ts";
import { usePermission } from "../../../lib/permissions.ts";
import { downloadItemsXlsx } from "./list-export.ts";
import { EmptyState } from "./empty-state.tsx";
import "./items-list-page.css";

type ListRow = z.infer<typeof itemsContract.listRow>;
type CreateItemInput = z.infer<typeof itemsContract.createInput>;
type TabId = "all" | "goods" | "services" | "low_stock" | "untagged" | "archived";
type TaxFilter = "all" | "taxable" | "non_taxable" | "gst_0" | "gst_5" | "gst_12" | "gst_18";
type SortKey = "recent" | "name_asc" | "rate_desc" | "margin_desc";

const TAX_FILTER_LABEL: Record<Exclude<TaxFilter, "all">, string> = {
  taxable: "Taxable",
  non_taxable: "Non-taxable",
  gst_0: "GST 0%",
  gst_5: "GST 5%",
  gst_12: "GST 12%",
  gst_18: "GST 18%",
};
type ImportPreviewRow = {
  line: number;
  raw: Record<string, string>;
  payload: CreateItemInput | undefined;
  errors: string[];
};

const TABS: Array<{ id: TabId; label: string }> = [
  { id: "all", label: "All" },
  { id: "goods", label: "Goods" },
  { id: "services", label: "Services" },
  { id: "low_stock", label: "Low stock" },
  { id: "untagged", label: "Untagged" },
  { id: "archived", label: "Archived" },
];

const TAX_FILTERS: Array<{ value: TaxFilter; label: string }> = [
  { value: "all", label: "GST · all rates" },
  { value: "taxable", label: "Taxable only" },
  { value: "non_taxable", label: "Exempt / nil / non-GST" },
  { value: "gst_0", label: "GST 0%" },
  { value: "gst_5", label: "GST 5%" },
  { value: "gst_12", label: "GST 12%" },
  { value: "gst_18", label: "GST 18%" },
];

const SORT_OPTIONS: Array<{ value: SortKey; label: string }> = [
  { value: "recent", label: "Sort · recent" },
  { value: "name_asc", label: "Name A-Z" },
  { value: "rate_desc", label: "Rate high-low" },
  { value: "margin_desc", label: "Margin high-low" },
];

const CSV_TEMPLATE_HEADERS = [
  "name",
  "sku",
  "itemType",
  "unit",
  "hsn",
  "category",
  "brand",
  "defaultRate",
  "defaultTaxRate",
  "usage",
  "taxPreference",
  "trackStock",
  "reorderLevel",
  "lastCost",
] as const;

export interface ItemsListPageProps {
  /** Initial tab (route may decode from URL search params). */
  initialTab?: TabId;
  /** Initial search text. */
  initialQuery?: string;
}

export function ItemsListPage({ initialTab = "all", initialQuery = "" }: ItemsListPageProps) {
  const canManage = usePermission("items.manage");
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const [tab, setTab] = useState<TabId>(normalizeTab(initialTab));
  const [draftQuery, setDraftQuery] = useState(initialQuery);
  const [committedQuery, setCommittedQuery] = useState(initialQuery);
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [taxFilter, setTaxFilter] = useState<TaxFilter>("all");
  const [vendorFilter, setVendorFilter] = useState("all");
  const [sortKey, setSortKey] = useState<SortKey>("recent");
  const [view, setView] = useState<"list" | "grid">("list");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [importOpen, setImportOpen] = useState(false);
  const [importPreview, setImportPreview] = useState<ImportPreviewRow[]>([]);
  const [importError, setImportError] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const createItem = trpc.items.create.useMutation();

  useEffect(() => {
    setTab(normalizeTab(initialTab));
    setDraftQuery(initialQuery);
    setCommittedQuery(initialQuery);
  }, [initialTab, initialQuery]);

  // Live search — debounce draftQuery → committedQuery so filtering
  // fires on every keystroke without hammering the API.
  useEffect(() => {
    const trimmed = draftQuery.trim();
    if (trimmed === committedQuery) return;
    const t = setTimeout(() => setCommittedQuery(trimmed), 250);
    return () => clearTimeout(t);
  }, [draftQuery, committedQuery]);

  // Cursor-stack pagination (the shared list pattern — see the PO list).
  const PAGE_SIZE = 100;
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const cursor = cursorStack[cursorStack.length - 1];
  useEffect(() => {
    setCursorStack([]);
  }, [tab, committedQuery, categoryFilter, taxFilter, vendorFilter, sortKey]);

  const list = trpc.items.list.useQuery(
    {
      limit: PAGE_SIZE,
      tab,
      ...(committedQuery ? { q: committedQuery } : {}),
      ...(cursor !== undefined ? { cursor } : {}),
    },
    { placeholderData: (prev) => prev },
  );
  const counts = trpc.items.counts.useQuery();
  const items = (list.data?.items as ListRow[] | undefined) ?? [];
  const categoryOptions = useMemo(() => {
    return [...new Set(items.map((item) => item.category).filter(Boolean) as string[])].sort((a, b) =>
      a.localeCompare(b),
    );
  }, [items]);
  const vendorOptions = useMemo(() => {
    const options = new Map<string, string>();
    for (const item of items) {
      if (item.preferredVendorId && item.preferredVendorName) {
        options.set(item.preferredVendorId, item.preferredVendorName);
      }
    }
    return [...options.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [items]);
  const filteredItems = useMemo(() => {
    const visible = items.filter((item) => {
      if (categoryFilter !== "all" && item.category !== categoryFilter) return false;
      if (vendorFilter !== "all" && item.preferredVendorId !== vendorFilter) return false;
      return matchesTaxFilter(item, taxFilter);
    });
    return sortItems(visible, sortKey);
  }, [items, categoryFilter, taxFilter, vendorFilter, sortKey]);
  const hasListCriteria =
    committedQuery !== "" ||
    tab !== "all" ||
    categoryFilter !== "all" ||
    taxFilter !== "all" ||
    vendorFilter !== "all";

  // Pagination derivations — page index = cursor-stack depth + 1.
  const pageNumber = cursorStack.length + 1;
  const showingFrom = filteredItems.length === 0 ? 0 : cursorStack.length * PAGE_SIZE + 1;
  const showingTo = cursorStack.length * PAGE_SIZE + filteredItems.length;
  const totalRows = hasListCriteria ? filteredItems.length : counts.data?.total;
  const handlePrev = () => setCursorStack((s) => s.slice(0, -1));
  const handleNext = () => {
    const next = list.data?.nextCursor;
    if (next) setCursorStack((s) => [...s, next]);
  };

  const onRowSelect = (id: string, checked: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };
  const onSelectAll = (checked: boolean) => {
    setSelected(checked ? new Set(filteredItems.map((r) => r.id)) : new Set());
  };

  const submitSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setCommittedQuery(draftQuery.trim());
  };

  // Average margin across items that have both rate + cost on file. Bounded
  // to the current page of loaded items — good enough for the workspace-scale
  // this KPI targets. Returns null when nothing is priced, so the card can
  // render an honest empty state instead of a placeholder number.
  const { avgMarginPct, marginSampleSize } = useMemo(() => {
    const samples: number[] = [];
    for (const i of items) {
      const m = computeMarginPct(i.defaultRatePaise, i.lastCostPaise);
      if (m !== null) samples.push(m);
    }
    if (samples.length === 0) return { avgMarginPct: null as number | null, marginSampleSize: 0 };
    const mean = samples.reduce((a, b) => a + b, 0) / samples.length;
    return { avgMarginPct: Math.round(mean), marginSampleSize: samples.length };
  }, [items]);

  // Counts per tab come from the server aggregate so switching tabs never
  // changes the labels — `items` on the current page is scoped to the
  // active tab, so reading tab-specific counts from it lied.
  const tabCounts = useMemo(() => {
    const c = counts.data;
    return {
      all: c?.total ?? 0,
      archived: c?.archived ?? 0,
      goods: c?.goods ?? 0,
      services: c?.services ?? 0,
      low_stock: c?.lowStock ?? 0,
      untagged: c?.untagged ?? 0,
    };
  }, [counts.data]);

  const clearCriteria = () => {
    setDraftQuery("");
    setCommittedQuery("");
    setCategoryFilter("all");
    setTaxFilter("all");
    setVendorFilter("all");
  };

  const handleDownloadTemplate = () => {
    const csv = [
      CSV_TEMPLATE_HEADERS.join(","),
      [
        "Sample Cotton Fabric",
        "QA-SAMPLE-001",
        "goods",
        "m",
        "5208",
        "Cotton",
        "Vardhman",
        "120.00",
        "18",
        "both",
        "taxable",
        "true",
        "10",
        "96.00",
      ]
        .map(csvCell)
        .join(","),
    ].join("\r\n");
    downloadBlob(csv, "flint-items-template.csv", "text/csv;charset=utf-8");
  };

  const handleExportCsv = () => {
    downloadBlob(rowsToCsv(filteredItems), `flint-items-${todayStamp()}.csv`, "text/csv;charset=utf-8");
    toast.success("Items CSV downloaded", { description: `${filteredItems.length} row(s)` });
  };

  const handleExportExcel = async () => {
    const vendorLabel =
      vendorFilter === "all"
        ? null
        : (vendorOptions.find(([id]) => id === vendorFilter)?.[1] ?? null);
    try {
      await downloadItemsXlsx(filteredItems, `flint-items-${todayStamp()}.xlsx`, {
        tab,
        search: committedQuery,
        category: categoryFilter === "all" ? null : categoryFilter,
        gst: taxFilter === "all" ? null : TAX_FILTER_LABEL[taxFilter],
        vendor: vendorLabel,
      });
      toast.success("Items Excel downloaded", { description: `${filteredItems.length} row(s)` });
    } catch (err) {
      toast.error("Couldn't build Excel file", {
        description: err instanceof Error ? err.message : "Try again in a moment.",
      });
    }
  };

  const handleImportFile = async (file: File | null) => {
    setImportError(null);
    setImportPreview([]);
    if (!file) return;
    try {
      setImportPreview(parseItemsCsv(await file.text()));
    } catch (err) {
      setImportError(err instanceof Error ? err.message : "Couldn't parse that CSV.");
    }
  };

  const handleImport = async () => {
    const validRows = importPreview.filter((row): row is ImportPreviewRow & { payload: CreateItemInput } =>
      row.payload !== undefined && row.errors.length === 0,
    );
    if (validRows.length === 0) {
      setImportError("No valid rows to import.");
      return;
    }
    setImporting(true);
    try {
      for (const row of validRows) {
        await createItem.mutateAsync(row.payload);
      }
      await Promise.all([utils.items.list.invalidate(), utils.items.counts.invalidate()]);
      setImportOpen(false);
      setImportPreview([]);
      if (fileInputRef.current) fileInputRef.current.value = "";
      toast.success("Items imported", { description: `${validRows.length} row(s) added.` });
    } catch (err) {
      setImportError(err instanceof Error ? err.message : "Couldn't import the CSV.");
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="items-list-page space-y-0 px-6 pt-5 pb-16 max-w-[1320px] mx-auto w-full">
      {/* page-head */}
      <header className="page-head">
        <div className="page-head-text">
          <h1>Items</h1>
          <p className="lead">
            <span>
              <span className="num" style={{ color: "var(--ink)", fontWeight: 600 }}>
                {counts.data?.total ?? "—"}
              </span>{" "}
              active
            </span>
            <span className="dot" aria-hidden />
            <span>
              <span className="num" style={{ color: "var(--ink)", fontWeight: 600 }}>
                {counts.data?.categories ?? "—"}
              </span>{" "}
              categories
            </span>
            <span className="dot" aria-hidden />
            <span>
              last touched <span className="num">today</span>
            </span>
          </p>
        </div>
        <div className="page-head-actions">
          {canManage && (
            <>
              <button
                type="button"
                onClick={() => setImportOpen(true)}
                className="btn-secondary inline-flex items-center gap-1.5 h-9 px-3.5 rounded-md border border-line bg-paper text-xs font-semibold text-ink-2"
              >
                <Upload size={13} aria-hidden />
                Import CSV
              </button>
              <Link
                to="/sales/items/new"
                className="btn-primary inline-flex items-center gap-1.5 h-9 px-3.5 rounded-md bg-ink text-paper text-sm font-semibold hover:bg-ink-2"
              >
                <Plus size={13} aria-hidden />
                New item
              </Link>
            </>
          )}
        </div>
      </header>

      {/* KPI strip — 5 cards */}
      <section className="kpis" aria-label="Items KPIs">
        <KpiCard
          stripe="s-indigo"
          label="Active items"
          icon={<Package size={14} />}
          value={String(counts.data?.total ?? 0)}
          meta={
            <>
              <span className="delta up">+4</span>
              <span aria-hidden>·</span>
              <span>added this quarter</span>
            </>
          }
        />
        <KpiCard
          stripe="s-emerald"
          label="Avg margin · sold"
          icon={<Hammer size={14} />}
          value={
            avgMarginPct === null ? "—" : String(avgMarginPct)
          }
          unit={avgMarginPct === null ? "no cost on file" : "%"}
          meta={
            avgMarginPct === null ? (
              <span>Add last cost on items to unlock margin</span>
            ) : (
              <>
                <span>Across {marginSampleSize} priced items</span>
              </>
            )
          }
        />
        <KpiCard
          stripe="s-amber"
          label="Reorder watchlist"
          icon={<Filter size={14} />}
          value={String(counts.data?.reorderTracked ?? 0)}
          unit="tracked"
          meta={
            <>
              <span className="future">V1.x</span>
              <span>balances unlock with stock module</span>
            </>
          }
        />
        <KpiCard
          stripe="s-violet"
          label="Catalogue health"
          icon={<SlidersHorizontal size={14} />}
          value={
            items.length
              ? String(
                  Math.round(
                    (items.filter((i) => i.description !== null).length / items.length) * 100,
                  ),
                )
              : "—"
          }
          unit="% described"
          meta={
            <>
              {items.filter((i) => i.hsn !== null).length} / {items.length} with HSN
            </>
          }
        />
        <KpiCard
          stripe="s-rose"
          label="Stale"
          icon={<Filter size={14} />}
          value="2"
          unit="items"
          meta={<>not used in 90+ days · review or archive</>}
        />
      </section>

      {/* Tabs */}
      <TabsRoot
        className="mb-3.5"
        value={tab}
        onValueChange={(v) => setTab(v as TabId)}
      >
        <TabsList aria-label="Item type">
          {TABS.map((t) => (
            <TabsTrigger key={t.id} value={t.id} count={tabCounts[t.id] ?? 0}>
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>
        {/* Each Radix TabsTrigger sets aria-controls to a panel id; the toolbar +
            table below aren't Radix panels, so without these the references
            dangle (axe aria-valid-attr-value, critical). forceMount keeps an
            empty, screen-reader-hidden panel per tab so every reference resolves. */}
        {TABS.map((t) => (
          <TabsContent key={t.id} value={t.id} forceMount className="hidden" aria-hidden />
        ))}
      </TabsRoot>

      {/* Toolbar */}
      <form className="toolbar" onSubmit={submitSearch}>
        <label className="search-input">
          <Search size={14} aria-hidden />
          <input
            type="search"
            placeholder="Search by name, SKU, barcode, brand…"
            value={draftQuery}
            onChange={(e) => setDraftQuery(e.target.value)}
            aria-label="Search items"
          />
        </label>
        <FilterSelect
          ariaLabel="Category filter"
          icon={<Filter size={12} aria-hidden />}
          value={categoryFilter}
          onValueChange={setCategoryFilter}
        >
          <SelectItem value="all">Category · all</SelectItem>
          {categoryOptions.map((category) => (
            <SelectItem key={category} value={category}>
              {category}
            </SelectItem>
          ))}
        </FilterSelect>
        <FilterSelect
          ariaLabel="GST filter"
          icon={<Filter size={12} aria-hidden />}
          value={taxFilter}
          onValueChange={(value) => setTaxFilter(value as TaxFilter)}
        >
          {TAX_FILTERS.map((filter) => (
            <SelectItem key={filter.value} value={filter.value}>
              {filter.label}
            </SelectItem>
          ))}
        </FilterSelect>
        <FilterSelect
          ariaLabel="Vendor filter"
          icon={<Filter size={12} aria-hidden />}
          value={vendorFilter}
          onValueChange={setVendorFilter}
        >
          <SelectItem value="all">Vendor · all</SelectItem>
          {vendorOptions.map(([vendorId, vendorName]) => (
            <SelectItem key={vendorId} value={vendorId}>
              {vendorName}
            </SelectItem>
          ))}
        </FilterSelect>
        <FilterSelect
          ariaLabel="Sort items"
          icon={<SlidersHorizontal size={12} aria-hidden />}
          value={sortKey}
          onValueChange={(value) => setSortKey(value as SortKey)}
        >
          {SORT_OPTIONS.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </FilterSelect>
        <span className="toolbar-spacer" />
        <div className="view-toggle" role="group" aria-label="View">
          <button
            type="button"
            className={view === "list" ? "is-active" : ""}
            aria-label="List view"
            aria-pressed={view === "list"}
            onClick={() => setView("list")}
          >
            <ListIcon size={14} aria-hidden />
          </button>
          <button
            type="button"
            className={view === "grid" ? "is-active" : ""}
            aria-label="Grid view"
            aria-pressed={view === "grid"}
            onClick={() => setView("grid")}
          >
            <LayoutGrid size={14} aria-hidden />
          </button>
        </div>
        {canManage ? (
          <DropdownMenuRoot>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="btn-secondary inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-line bg-paper text-xs font-semibold text-ink-2"
              >
                <Download size={12} aria-hidden /> Export
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
              <DropdownMenuItem onSelect={handleExportCsv}>Export CSV</DropdownMenuItem>
              <DropdownMenuItem onSelect={handleExportExcel}>Export Excel</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenuRoot>
        ) : null}
      </form>

      {/* Bulk-action bar */}
      {selected.size > 0 ? (
        <div className="bulk-bar show" role="region" aria-label="Bulk actions">
          <span className="count-chip">{selected.size}</span>
          <span>items selected</span>
          <span className="spacer" />
          <button type="button" disabled>
            Edit tags
          </button>
          <button type="button" disabled>
            Change category
          </button>
          <button type="button" disabled>
            Set vendor
          </button>
          <button type="button" disabled>
            Archive
          </button>
        </div>
      ) : null}

      {/* Body */}
      {list.isLoading ? (
        <p className="text-muted">Loading items…</p>
      ) : list.error ? (
        <div className="items-list">
          <div className="empty">
            <h3>Couldn't load items</h3>
            <p>{list.error.message}</p>
          </div>
        </div>
      ) : filteredItems.length === 0 ? (
        (() => {
          const hasQuery = committedQuery !== "";
          const hasFilter =
            categoryFilter !== "all" || taxFilter !== "all" || vendorFilter !== "all";
          if (hasQuery) return <EmptyState variant="no-search-match" query={committedQuery} />;
          if (hasFilter) return <EmptyState variant="no-filter-match" onClearFilters={clearCriteria} />;
          if (tab === "archived") return <EmptyState variant="no-archived" />;
          if (tab !== "all") return <EmptyState variant="no-tab-match" tab={tab} />;
          return <EmptyState variant="first-run" canManage={canManage} />;
        })()
      ) : view === "grid" ? (
        <div className="items-grid">
          {filteredItems.map((row) => (
            <GridCard
              key={row.id}
              row={row}
              onOpen={() => void navigate({ to: "/sales/items/$itemId", params: { itemId: row.id } })}
            />
          ))}
        </div>
      ) : (
        <DataTableCard
          footer={[
            {
              label: "Showing",
              value: `${filteredItems.length} / ${counts.data?.total ?? "?"}`,
            },
            { label: "Goods", value: tabCounts.goods },
            { label: "Services", value: tabCounts.services },
            {
              label: "With vendor",
              value: `${filteredItems.filter((i) => i.preferredVendorId !== null).length} / ${filteredItems.length}`,
            },
          ]}
          pagination={
            <ListPagination
              pageNumber={pageNumber}
              showingFrom={showingFrom}
              showingTo={showingTo}
              {...(typeof totalRows === "number" ? { total: totalRows } : {})}
              sortLabel="sorted by recently added"
              hasPrev={cursorStack.length > 0}
              hasNext={!!list.data?.nextCursor}
              onPrev={handlePrev}
              onNext={handleNext}
            />
          }
        >
          <table className="items-table" aria-label="Items">
            <thead>
              <tr>
                <th className="sel" aria-label="Select">
                  <input
                    type="checkbox"
                    aria-label="Select all"
                    checked={selected.size > 0 && selected.size === filteredItems.length}
                    onChange={(e) => onSelectAll(e.target.checked)}
                  />
                </th>
                <th>Item</th>
                <th className="col-category">Category</th>
                <th className="col-hsn">HSN</th>
                <th className="col-stock">Stock</th>
                <th className="col-cost right">Cost</th>
                <th className="right">Rate</th>
                <th className="right">Margin</th>
                <th className="col-vendor">Vendor</th>
                <th className="col-updated">Updated</th>
                <th aria-label="Actions"></th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.map((row) => (
                <ItemTableRow
                  key={row.id}
                  row={row}
                  selected={selected.has(row.id)}
                  onSelect={(checked) => onRowSelect(row.id, checked)}
                  onOpen={() => void navigate({ to: "/sales/items/$itemId", params: { itemId: row.id } })}
                  onCategoryClick={(category) => setCategoryFilter(category)}
                />
              ))}
            </tbody>
          </table>
        </DataTableCard>
      )}
      <ImportItemsDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        preview={importPreview}
        error={importError}
        importing={importing}
        fileInputRef={fileInputRef}
        onPickFile={(file) => void handleImportFile(file)}
        onDownloadTemplate={handleDownloadTemplate}
        onImport={() => void handleImport()}
      />
    </div>
  );
}

// ── KPI card helper ────────────────────────────────────────────

function KpiCard(props: {
  stripe: string;
  label: string;
  icon: React.ReactNode;
  value: string;
  unit?: string;
  meta?: React.ReactNode;
}) {
  return (
    <div className={`kpi ${props.stripe}`}>
      <div className="kpi-stripe" aria-hidden />
      <div className="kpi-head">
        <span>{props.label}</span>
        <span className="ic" aria-hidden>
          {props.icon}
        </span>
      </div>
      <div className="kpi-val">
        <span className="num">{props.value}</span>
        {props.unit ? <span className="u">{props.unit}</span> : null}
      </div>
      {props.meta ? <div className="kpi-meta">{props.meta}</div> : null}
    </div>
  );
}

// ── Row / grid card helpers ────────────────────────────────────

function FilterSelect({
  ariaLabel,
  icon,
  value,
  onValueChange,
  children,
}: {
  ariaLabel: string;
  icon: React.ReactNode;
  value: string;
  onValueChange: (value: string) => void;
  children: React.ReactNode;
}) {
  return (
    <Select value={value} onValueChange={onValueChange}>
      <SelectTrigger className="filter-pill" aria-label={ariaLabel}>
        <span className="filter-pill-icon" aria-hidden>
          {icon}
        </span>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>{children}</SelectContent>
    </Select>
  );
}

function ImportItemsDialog({
  open,
  onOpenChange,
  preview,
  error,
  importing,
  fileInputRef,
  onPickFile,
  onDownloadTemplate,
  onImport,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  preview: ImportPreviewRow[];
  error: string | null;
  importing: boolean;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  onPickFile: (file: File | null) => void;
  onDownloadTemplate: () => void;
  onImport: () => void;
}) {
  const validCount = preview.filter((row) => row.payload && row.errors.length === 0).length;
  const errorCount = preview.length - validCount;
  return (
    <DialogRoot open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[680px]">
        <DialogHeader>
          <DialogTitle>Import items from CSV</DialogTitle>
          <DialogDescription>
            Upload item master rows for this workspace. Flint validates each row before import.
          </DialogDescription>
        </DialogHeader>
        <div className="items-import-dialog">
          <div className="import-source-grid">
            <button type="button" className="import-source is-active">
              <Upload size={16} aria-hidden />
              <span>
                <strong>Local CSV</strong>
                <small>Upload a file from this device.</small>
              </span>
            </button>
            <button
              type="button"
              className="import-source"
              disabled
              title="Drive/source connector lands with integrations."
            >
              <Upload size={16} aria-hidden />
              <span>
                <strong>Drive/source</strong>
                <small>Connector not connected yet.</small>
              </span>
            </button>
          </div>
          <div className="import-actions">
            <button type="button" className="btn-secondary" onClick={onDownloadTemplate}>
              Download template
            </button>
            <label className="file-picker">
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                onChange={(event) => onPickFile(event.target.files?.[0] ?? null)}
              />
              Choose CSV file
            </label>
          </div>
          {error ? <p className="import-error">{error}</p> : null}
          {preview.length > 0 ? (
            <div className="import-preview" aria-live="polite">
              <p>
                {validCount} importable row{validCount === 1 ? "" : "s"}
                {errorCount > 0 ? ` · ${errorCount} row${errorCount === 1 ? "" : "s"} need fixes` : ""}
              </p>
              <div className="import-preview-table">
                <table>
                  <thead>
                    <tr>
                      <th>Line</th>
                      <th>Name</th>
                      <th>SKU</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.slice(0, 8).map((row) => (
                      <tr key={row.line}>
                        <td>{row.line}</td>
                        <td>{row.raw.name || "—"}</td>
                        <td>{row.raw.sku || "—"}</td>
                        <td>{row.errors.length > 0 ? row.errors.join("; ") : "Ready"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}
        </div>
        <DialogFooter>
          <button type="button" className="btn-secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary"
            disabled={validCount === 0 || importing}
            onClick={onImport}
          >
            {importing ? "Importing..." : `Import ${validCount} row${validCount === 1 ? "" : "s"}`}
          </button>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
}

function ItemTableRow({
  row,
  selected,
  onSelect,
  onOpen,
  onCategoryClick,
}: {
  row: ListRow;
  selected: boolean;
  onSelect: (checked: boolean) => void;
  onOpen: () => void;
  onCategoryClick: (category: string) => void;
}) {
  const rateLabel = row.defaultRatePaise
    ? `₹${(Number(BigInt(row.defaultRatePaise)) / 100).toLocaleString("en-IN", { minimumFractionDigits: 2 })} / ${row.unit}`
    : "—";
  const costLabel = row.lastCostPaise
    ? `₹${(Number(BigInt(row.lastCostPaise)) / 100).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`
    : "—";
  const margin = computeMarginPct(row.defaultRatePaise, row.lastCostPaise);
  const marginCls = margin === null ? "na" : margin >= 20 ? "good" : margin >= 10 ? "warn" : "bad";
  return (
    <tr className={selected ? "is-selected" : ""} onClick={onOpen}>
      <td className="sel" onClick={(e) => e.stopPropagation()}>
        <input
          type="checkbox"
          aria-label={`Select ${row.name}`}
          checked={selected}
          onChange={(e) => onSelect(e.target.checked)}
        />
      </td>
      <td>
        <div className="item-cell">
          <span
            aria-hidden
            className={`grid h-8 w-8 shrink-0 place-items-center rounded-md text-xs font-bold text-white ${avatarClassFor(row.name)}`}
          >
            {initialsFor(row.name)}
          </span>
          <div className="stack">
            <span className="name">{row.name}</span>
            <span className="meta">
              {row.sku ? <span className="sku">{row.sku}</span> : null}
              <span className="sep" aria-hidden>·</span>
              <span className="unit-chip">{row.unit}</span>
              {row.brand ? (
                <>
                  <span>·</span>
                  <span className="brand">{row.brand}</span>
                </>
              ) : null}
              {row.usage ? (
                <>
                  <span>·</span>
                  <span className={`usage-chip ${row.usage}`}>
                    {row.usage[0]!.toUpperCase() + row.usage.slice(1)}
                  </span>
                </>
              ) : null}
            </span>
          </div>
        </div>
      </td>
      <td className="col-category" onClick={(e) => e.stopPropagation()}>
        {row.category ? (
          <button
            type="button"
            onClick={() => onCategoryClick(row.category!)}
            title={`Filter list by category "${row.category}"`}
            className={`inline-flex items-center px-2 py-0.5 rounded-lg text-xs font-semibold hover:ring-1 hover:ring-line ${categoryChipClass(row.category)}`}
          >
            {row.category}
          </button>
        ) : (
          <span className="text-xs text-muted">—</span>
        )}
      </td>
      <td className="col-hsn">
        <span className="hsn">{row.hsn ?? "—"}</span>
      </td>
      <td className="col-stock">
        {row.trackStock ? (
          <span className="stock-chip is-tracked">RP {row.reorderLevel ?? "—"}</span>
        ) : (
          <span className="stock-chip is-untracked">—</span>
        )}
      </td>
      <td className="col-cost right">{costLabel}</td>
      <td className="right">{rateLabel}</td>
      <td className="right">
        <span className={`margin-pill ${marginCls}`}>{margin === null ? "—" : `${margin}%`}</span>
      </td>
      <td className="col-vendor">
        {row.preferredVendorName ? (
          <span className="vendor-cell">
            <span className="v-dot" />
            {row.preferredVendorName}
          </span>
        ) : (
          <span className="vendor-cell empty">no vendor</span>
        )}
      </td>
      <td className="col-updated">
        <span className="when">{formatRelativeDate(row.createdAt)}</span>
      </td>
      <td>
        <button
          type="button"
          className="arrow-btn"
          aria-label={`Open ${row.name}`}
          onClick={(e) => {
            e.stopPropagation();
            onOpen();
          }}
        >
          <ChevronRight size={14} aria-hidden />
        </button>
      </td>
    </tr>
  );
}

function GridCard({ row, onOpen }: { row: ListRow; onOpen: () => void }) {
  const rate = row.defaultRatePaise
    ? `₹${(Number(BigInt(row.defaultRatePaise)) / 100).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`
    : "—";
  return (
    <div
      className="grid-card"
      onClick={onOpen}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter") onOpen();
      }}
    >
      <div
        className={`thumb ${row.itemType === "service" ? "is-service" : "is-goods"}`}
        aria-hidden
      >
        {row.itemType === "service" ? <Hammer /> : <Package />}
      </div>
      <div className="min-w-0">
        <div className="gc-name" title={row.name}>{row.name}</div>
        {row.sku ? <div className="gc-sku">{row.sku}</div> : null}
      </div>
      <div className="gc-bottom">
        <span className={`usage-chip ${row.usage}`}>
          {row.usage[0]!.toUpperCase() + row.usage.slice(1)}
        </span>
        <span className="gc-rate">{rate}</span>
      </div>
    </div>
  );
}

// ── helpers ────────────────────────────────────────────────────

function matchesTaxFilter(row: ListRow, filter: TaxFilter): boolean {
  if (filter === "all") return true;
  if (filter === "taxable") return row.taxPreference === "taxable";
  if (filter === "non_taxable") return row.taxPreference !== "taxable";
  const bps = row.defaultTaxRateBps ?? 0;
  if (filter === "gst_0") return bps === 0;
  if (filter === "gst_5") return bps === 500;
  if (filter === "gst_12") return bps === 1200;
  return bps === 1800;
}

function sortItems(rows: ListRow[], sortKey: SortKey): ListRow[] {
  const sorted = [...rows];
  if (sortKey === "name_asc") return sorted.sort((a, b) => a.name.localeCompare(b.name));
  if (sortKey === "rate_desc") {
    return sorted.sort((a, b) => paiseNumber(b.defaultRatePaise) - paiseNumber(a.defaultRatePaise));
  }
  if (sortKey === "margin_desc") {
    return sorted.sort(
      (a, b) =>
        (computeMarginPct(b.defaultRatePaise, b.lastCostPaise) ?? -1) -
        (computeMarginPct(a.defaultRatePaise, a.lastCostPaise) ?? -1),
    );
  }
  return sorted.sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt));
}

function paiseNumber(value: string | null): number {
  return value ? Number(BigInt(value)) : 0;
}

function rowsToCsv(rows: ListRow[]): string {
  const header = [
    "Name",
    "SKU",
    "Type",
    "Category",
    "Brand",
    "HSN",
    "Unit",
    "Stock",
    "Cost",
    "Rate",
    "GST %",
    "Margin %",
    "Vendor",
    "Updated",
  ];
  const lines = rows.map((row) =>
    [
      row.name,
      row.sku ?? "",
      row.itemType,
      row.category ?? "",
      row.brand ?? "",
      row.hsn ?? "",
      row.unit,
      row.trackStock ? String(row.reorderLevel ?? "") : "",
      moneyForExport(row.lastCostPaise),
      moneyForExport(row.defaultRatePaise),
      row.defaultTaxRateBps === null ? "" : String(row.defaultTaxRateBps / 100),
      computeMarginPct(row.defaultRatePaise, row.lastCostPaise)?.toString() ?? "",
      row.preferredVendorName ?? "",
      formatRelativeDate(row.createdAt),
    ]
      .map(csvCell)
      .join(","),
  );
  return [header.map(csvCell).join(","), ...lines].join("\r\n");
}

function downloadBlob(content: string, fileName: string, type: string) {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  URL.revokeObjectURL(url);
}

function todayStamp(): string {
  return new Date().toISOString().slice(0, 10);
}

function moneyForExport(value: string | null): string {
  if (!value) return "";
  return (Number(BigInt(value)) / 100).toFixed(2);
}

function csvCell(value: string): string {
  return /[",\r\n]/.test(value) ? `"${value.replaceAll('"', '""')}"` : value;
}

function parseItemsCsv(text: string): ImportPreviewRow[] {
  const rows = parseCsv(text);
  if (rows.length < 2) throw new Error("CSV needs a header row and at least one item row.");
  const headers = rows[0]!.map((header) => normalizeHeader(header));
  if (!headers.includes("name")) throw new Error("CSV must include a name column.");
  return rows
    .slice(1)
    .filter((row) => row.some((cell) => cell.trim() !== ""))
    .map((row, index) => {
      const raw: Record<string, string> = {};
      headers.forEach((header, cellIndex) => {
        raw[header] = row[cellIndex]?.trim() ?? "";
      });
      const errors: string[] = [];
      const name = raw.name ?? "";
      if (!name) errors.push("Name is required");
      const itemType = parseEnum(raw.itemtype, ["goods", "service", "bundle"], "goods");
      if (itemType === "bundle") errors.push("Bundle items are not supported yet");
      const usage = parseEnum(raw.usage, ["sale", "purchase", "both"], "sale");
      const taxPreference = parseEnum(
        raw.taxpreference,
        ["taxable", "exempt", "nil_rated", "non_gst"],
        "taxable",
      );
      const defaultRatePaise = parseOptionalPaise(raw.defaultrate, "Default rate", errors);
      const lastCostPaise = parseOptionalPaise(raw.lastcost, "Last cost", errors);
      const defaultTaxRateBps = parseOptionalBps(raw.defaulttaxrate, "Default GST", errors);
      const reorderLevel = parseOptionalInteger(raw.reorderlevel, "Reorder level", errors);
      const payload: CreateItemInput | undefined = errors.length
        ? undefined
        : compactCreateInput({
            name,
            sku: raw.sku,
            itemType,
            unit: raw.unit || "Nos",
            hsn: raw.hsn,
            category: raw.category,
            brand: raw.brand,
            defaultRatePaise,
            defaultTaxRateBps,
            usage,
            taxPreference,
            trackStock: parseBoolean(raw.trackstock),
            reorderLevel,
            lastCostPaise,
          });
      return { line: index + 2, raw, payload, errors };
    });
}

function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let quoted = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i]!;
    if (quoted) {
      if (ch === '"' && text[i + 1] === '"') {
        cell += '"';
        i++;
      } else if (ch === '"') quoted = false;
      else cell += ch;
    } else if (ch === '"') quoted = true;
    else if (ch === ",") {
      row.push(cell);
      cell = "";
    } else if (ch === "\n") {
      row.push(cell.replace(/\r$/, ""));
      rows.push(row);
      row = [];
      cell = "";
    } else cell += ch;
  }
  row.push(cell.replace(/\r$/, ""));
  rows.push(row);
  return rows;
}

function normalizeHeader(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]/g, "");
}

function parseEnum<T extends string>(value: string | undefined, allowed: readonly T[], fallback: T): T {
  const normalized = value?.trim().toLowerCase();
  return allowed.includes(normalized as T) ? (normalized as T) : fallback;
}

function parseOptionalPaise(value: string | undefined, label: string, errors: string[]): string | undefined {
  if (!value) return undefined;
  const numeric = Number(value.replace(/,/g, ""));
  if (!Number.isFinite(numeric) || numeric < 0) {
    errors.push(`${label} must be a non-negative number`);
    return undefined;
  }
  return String(Math.round(numeric * 100));
}

function parseOptionalBps(value: string | undefined, label: string, errors: string[]): number | undefined {
  if (!value) return undefined;
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric < 0 || numeric > 100) {
    errors.push(`${label} must be between 0 and 100`);
    return undefined;
  }
  return Math.round(numeric * 100);
}

function parseOptionalInteger(value: string | undefined, label: string, errors: string[]): number | undefined {
  if (!value) return undefined;
  const numeric = Number(value);
  if (!Number.isInteger(numeric) || numeric < 0) {
    errors.push(`${label} must be a non-negative integer`);
    return undefined;
  }
  return numeric;
}

function parseBoolean(value: string | undefined): boolean | undefined {
  if (!value) return undefined;
  const normalized = value.trim().toLowerCase();
  if (["true", "yes", "1"].includes(normalized)) return true;
  if (["false", "no", "0"].includes(normalized)) return false;
  return undefined;
}

function normalizeTab(value: string | undefined): TabId {
  return TABS.some((tab) => tab.id === value) ? (value as TabId) : "all";
}

function compactCreateInput(input: CreateItemInput): CreateItemInput {
  return Object.fromEntries(
    Object.entries(input).filter(([, value]) => value !== undefined && value !== ""),
  ) as CreateItemInput;
}

function computeMarginPct(ratePaise: string | null, costPaise: string | null): number | null {
  if (!ratePaise || !costPaise) return null;
  const rate = BigInt(ratePaise);
  const cost = BigInt(costPaise);
  if (rate === 0n) return null;
  // (rate - cost) / rate * 100
  const numerator = (rate - cost) * 100n;
  const pct = Number(numerator) / Number(rate);
  return Math.round(pct);
}

const CATEGORY_PALETTES = [
  "bg-indigo-100 text-indigo-700",
  "bg-emerald-100 text-emerald-700",
  "bg-amber-100 text-amber-700",
  "bg-violet-100 text-violet-700",
  "bg-sky-100 text-sky-700",
  "bg-rose-100 text-rose-700",
  "bg-teal-100 text-teal-700",
  "bg-orange-100 text-orange-700",
] as const;

function categoryChipClass(category: string): string {
  let hash = 0;
  for (let i = 0; i < category.length; i++) hash = (hash * 31 + category.charCodeAt(i)) >>> 0;
  return CATEGORY_PALETTES[hash % CATEGORY_PALETTES.length]!;
}

function formatRelativeDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
}
